/**
 * Income Record Models
 * 
 * Unified income record types with tax classification at import time.
 */

import { IncomeType, TaxCategory, ExternalIncomeType, OptionOutcome } from '../enums/index.js';

/**
 * Base income record - all income types
 * Includes tax classification which is set at import/derivation time
 */
export interface IncomeRecord {
  id: string;
  userId: string;
  accountId?: string;
  sourceTransactionId: string;  // Links to originating transaction
  date: string;                  // ISO date string
  type: IncomeType;
  amount: number;
  symbol?: string;
  description?: string;
  taxCategory: TaxCategory;
  taxYear: number;
  quantity?: number;
  splitGroupId?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Extended type for option premiums with additional tracking fields
 */
export interface OptionIncomeRecord extends IncomeRecord {
  type: IncomeType.OptionPremium;
  isAssignedOptionPremium?: boolean;
  optionOutcome?: OptionOutcome;
  closingCost?: number;
  netOptionPnL?: number;
}

/**
 * Split portion for resolving an option with mixed outcomes
 * (e.g. 3 contracts bought to close, 2 assigned)
 */
export interface SplitPortion {
  action: 'expire' | 'close' | 'assign';
  quantity: number;
  closingCost?: number; // required when action is 'close'
}

/**
 * Result of splitting an income record into multiple records
 */
export interface SplitResolutionResult {
  originalRecordId: string;
  createdRecords: Array<{
    id: string;
    quantity: number;
    amount: number;
    outcome: string;
  }>;
}

/**
 * Type guard to check if an income record is an option premium
 */
export function isOptionIncomeRecord(record: IncomeRecord): record is OptionIncomeRecord {
  return record.type === IncomeType.OptionPremium;
}

/**
 * Conflict between existing and incoming income record
 * Follows the same pattern as TransactionConflict
 */
export interface IncomeRecordConflict {
  sourceTransactionId: string;
  existing: {
    id: string;
    type: IncomeType;
    amount: number;
    symbol?: string;
    taxCategory: TaxCategory;
    optionOutcome?: OptionOutcome;
    isAssignedOptionPremium?: boolean;
    closingCost?: number;
    netOptionPnL?: number;
  };
  incoming: {
    type: IncomeType;
    amount: number;
    symbol?: string;
    taxCategory: TaxCategory;
    optionOutcome?: OptionOutcome;
    isAssignedOptionPremium?: boolean;
    closingCost?: number;
    netOptionPnL?: number;
  };
  changedFields: string[];
}

// ============================================================================
// DTOs for Creating/Updating Income Records
// ============================================================================

/**
 * DTO for creating income record
 */
export interface CreateIncomeRecordDto {
  accountId?: string;
  sourceTransactionId: string;
  type: IncomeType;
  amount: number;
  date: Date | string;
  symbol?: string;
  description?: string;
  taxCategory: TaxCategory;
  taxYear?: number;  // Auto-calculated from date if not provided
  // Option-specific fields
  isAssignedOptionPremium?: boolean;
  optionOutcome?: OptionOutcome;
  closingCost?: number;
  netOptionPnL?: number;
  quantity?: number;
  splitGroupId?: string;
}

/**
 * DTO for updating income record
 * Users can edit taxCategory to override automatic classification
 */
export interface UpdateIncomeRecordDto {
  type?: IncomeType;
  amount?: number;
  date?: Date | string;
  symbol?: string;
  description?: string;
  taxCategory?: TaxCategory;  // Primary user-editable field
  taxYear?: number;
  // Option-specific fields
  isAssignedOptionPremium?: boolean;
  optionOutcome?: OptionOutcome;
  closingCost?: number;
  netOptionPnL?: number;
  quantity?: number;
}

// ============================================================================
// Income Summary (Aggregated for reporting)
// ============================================================================

/**
 * Income summary by year - aggregated for reporting/dashboard
 */
export interface IncomeSummary {
  taxYear: number;
  totalDividends: number;
  qualifiedDividends: number;
  ordinaryDividends: number;
  interest: number;
  shortTermCapitalGains: number;
  longTermCapitalGains: number;
  returnOfCapital: number;
  optionPremiums: number;
  other: number;
  total: number;
}

// ============================================================================
// External Income (Non-Portfolio Income) - unchanged
// ============================================================================

// Estimated tax breakdown (all fields editable by user)
export interface EstimatedTaxBreakdown {
  socialSecurity: number;      // 6.2% up to wage base
  medicare: number;            // 1.45% + 0.9% additional
  selfEmployment: number;      // 15.3% for 1099 (includes SS + Medicare)
  federal: number;             // Federal income tax
  state: number;               // State income tax
  niit: number;                // 3.8% Net Investment Income Tax
  total: number;               // Sum of all taxes
}

// Recurring paycheck configuration (W-2 only)
export interface RecurringPayConfig {
  isRecurring: boolean;
  frequency: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly';
  annualSalary: number;
  retirement401kPercent: number;  // 0-100
  startDate?: string;             // ISO date, defaults to Jan 1
  endDate?: string;               // ISO date, defaults to Dec 31
  includeProjected: boolean;      // Include future paychecks through year-end
}

// External income source interface
// Matches API response from backend (transformed from database schema)
export interface ExternalIncomeSource {
  id: string;
  type: ExternalIncomeType;
  description: string;

  // Income amounts
  grossAmount: number;           // Gross proceeds or gross pay
  costBasis?: number;            // For stock sales: original cost
  computedGain?: number;         // For stock sales: grossAmount - costBasis

  // Dates
  date: string;                  // ISO date of income/sale
  acquisitionDate?: string;      // For stock sales: when acquired
  holdingPeriodMonths?: number;  // Auto-calculated from dates

  // Tax estimates: computed by the server from the user's settings unless
  // `estimatedTaxesManual` (the user entered them by hand, WEBD-02).
  estimatedTaxes: EstimatedTaxBreakdown;
  estimatedTaxesManual?: boolean;

  // W-2 only: Whether employer withholds taxes (default true for W-2)
  taxesPaid: boolean;

  // W-2 only: Additional voluntary withholding amount per paycheck
  additionalWithholding?: number;

  // Recurring config (W-2 only) - nested structure for cleaner API
  recurringConfig?: RecurringPayConfig;

  // Optional tracking
  isQsbsEligible?: boolean;      // Section 1202 qualified small business stock

  // ISO exercise fields (for AMT preference item calculation)
  isoShares?: number;            // Number of shares exercised
  isoExercisePrice?: number;     // Exercise price per share (strike price)
  isoFmvAtExercise?: number;     // Fair market value per share at exercise date

  // Metadata
  createdAt: string;
  updatedAt: string;
}

// Display labels for external income types (used in UI)
export const EXTERNAL_INCOME_TYPE_LABELS: Record<ExternalIncomeType, string> = {
  [ExternalIncomeType.W2]: 'W-2 Wages & Salary',
  [ExternalIncomeType.Form1099Nec]: '1099-NEC (Non-Employee Compensation)',
  [ExternalIncomeType.Form1099Misc]: '1099-MISC',
  [ExternalIncomeType.PrivateStockSaleLong]: 'External Capital Gains (Long-Term)',
  [ExternalIncomeType.PrivateStockSaleShort]: 'External Capital Gains (Short-Term)',
  [ExternalIncomeType.SavingsInterest]: 'Savings Account Interest',
  [ExternalIncomeType.Rental]: 'Rental Income',
  [ExternalIncomeType.K1]: 'Schedule K-1 (Partnership/S-Corp)',
  [ExternalIncomeType.IsoExercise]: 'ISO Exercise (Incentive Stock Options)',
  [ExternalIncomeType.Other]: 'Other External Income',
};
